import { create } from 'zustand';

/**
 * Notifications contributed by embedded apps via the `rumio:notifications`
 * postMessage (Apps Platform contract v1.10). Each installed app publishes
 * its own pre-formatted, host-renderable items; the host header's
 * NotificationBell aggregates them across apps into one dropdown.
 *
 * Cleared by `AppMountedRoute` when its iframe unmounts so stale
 * notifications don't outlive the app surface that produced them.
 *
 * Keyed by app `id` (Rumio-side UUID).
 */
export interface AppNotificationItem {
  /** Stable id the host sends back in `rumio:notification-action` clicks. */
  id: string;
  /** Pre-formatted human-readable summary. Host renders verbatim. */
  label: string;
  /** Optional secondary line (e.g. a date). */
  sublabel?: string;
  /** Optional short tag rendered next to the row (e.g. "pantry"). */
  badge?: string;
  /** True if the item is already read; renders dimmed and is excluded from unread count. */
  read?: boolean;
}

export interface AppNotificationsEntry {
  appId: string;
  appName: string;
  unreadCount: number;
  items: AppNotificationItem[];
}

interface AppNotificationsState {
  byAppId: Record<string, AppNotificationsEntry>;
  setForApp: (entry: AppNotificationsEntry) => void;
  clearForApp: (appId: string) => void;
}

export const useAppNotificationsStore = create<AppNotificationsState>()((set) => ({
  byAppId: {},
  setForApp: (entry) =>
    set((s) => ({ byAppId: { ...s.byAppId, [entry.appId]: entry } })),
  clearForApp: (appId) =>
    set((s) => {
      if (!(appId in s.byAppId)) return s;
      const next = { ...s.byAppId };
      delete next[appId];
      return { byAppId: next };
    }),
}));
